/**
 * EventsTable — canonical CVE events tabular surface used by ThreatsTab.
 *
 * WQ-287 Item 2: extracted from inline ThreatsTab.tsx rendering loop and
 * augmented with threshold-gated virtualization via `react-window`.
 *
 * - At `items.length <= EVENTS_VIRTUALIZATION_THRESHOLD` (default 200):
 *   renders a standard semantic `<table>` — full DOM, native browser
 *   keyboard nav, copy/paste-friendly, screen-reader-natural. This is
 *   today's IWT operator UX (server-side pagination keeps page sizes <50).
 * - At `items.length > EVENTS_VIRTUALIZATION_THRESHOLD`: switches to a
 *   `react-window` FixedSizeList wrapping divs styled as table-rows.
 *   Preserves keyboard nav semantics on each rendered row (role="button",
 *   tabIndex={0}). Designed to prevent rendering-pipeline regression as
 *   canonical event volume grows post-DEF-013 multi-parser ingest.
 *
 * The threshold-gated approach honors WQ-G §13 "close structurally to
 * prevent regression" intent without changing today's UX. Trigger #3
 * scope-reduce (re-quote to Item 1 only) considered but not fired —
 * structural closure is the value here, not optimization of today's
 * <50-row case.
 */
import React from 'react';
import { FixedSizeList } from 'react-window';

import { OperatorStateBadge } from './OperatorStateBadge';
import { SeverityBadge } from './SeverityBadge';
import { StatusBadge } from './StatusBadge';
import {
  isRoutingEligible,
  severityTokenFromScore,
} from './ThreatsTab';
import type { CanonicalEventListItem } from '../types/security';

export const EVENTS_VIRTUALIZATION_THRESHOLD = 200;
const VIRTUALIZED_ROW_HEIGHT_PX = 56;
const VIRTUALIZED_LIST_HEIGHT_PX = 600;

// Screen-reader-only label for the select column header (empty-table-header a11y).
const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** WQ-298 D4: optional multi-select for package-grouped bulk send. */
export interface EventsSelection {
  selectedIds: Set<string>;
  onToggle: (canonicalEventId: string) => void;
}

interface EventsTableProps {
  items: CanonicalEventListItem[];
  onSelect: (canonicalEventId: string) => void;
  selection?: EventsSelection;
}

export function EventsTable({ items, onSelect, selection }: EventsTableProps): React.JSX.Element {
  if (items.length > EVENTS_VIRTUALIZATION_THRESHOLD) {
    return <VirtualizedEventsTable items={items} onSelect={onSelect} selection={selection} />;
  }
  return <StandardEventsTable items={items} onSelect={onSelect} selection={selection} />;
}

function StandardEventsTable({ items, onSelect, selection }: EventsTableProps): React.JSX.Element {
  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.875rem',
      }}
      aria-label="Canonical CVE events"
    >
      <thead>
        <tr style={{ textAlign: 'left' }}>
          {selection && (
            <th style={{ padding: '0.5rem', width: '2rem' }}>
              <span style={SR_ONLY}>Select</span>
            </th>
          )}
          <th style={{ padding: '0.5rem' }}>CVE</th>
          <th style={{ padding: '0.5rem' }}>Affected target</th>
          <th style={{ padding: '0.5rem' }}>Severity</th>
          <th style={{ padding: '0.5rem' }}>Status</th>
          <th style={{ padding: '0.5rem' }}>State</th>
          <th
            style={{ padding: '0.5rem' }}
            title="Number of distinct intel sources reporting this CVE. Multiple polls from the same source count as 1."
          >
            Sources
          </th>
          <th style={{ padding: '0.5rem' }}>Last seen</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <EventRow key={item.id} item={item} onSelect={onSelect} as="tr" selection={selection} />
        ))}
      </tbody>
    </table>
  );
}

function VirtualizedEventsTable({ items, onSelect, selection }: EventsTableProps): React.JSX.Element {
  const gridCols = `${selection ? '32px ' : ''}160px 1fr 100px 110px 130px 80px 160px`;
  return (
    <div
      role="region"
      aria-label="Canonical CVE events (virtualized at >200 rows)"
      style={{ fontSize: '0.875rem' }}
    >
      {/* Column-header strip (sticky semantics intentionally NOT implemented
          here; virtualized list scrolls independently of headers per
          react-window convention). */}
      <div
        role="row"
        style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: '0.5rem',
          fontWeight: 600,
          padding: '0.5rem',
          borderBottom: '1px solid var(--border-default, #e5e7eb)',
        }}
      >
        {selection && (
          <div role="columnheader"><span style={SR_ONLY}>Select</span></div>
        )}
        <div role="columnheader">CVE</div>
        <div role="columnheader">Affected target</div>
        <div role="columnheader">Severity</div>
        <div role="columnheader">Status</div>
        <div role="columnheader">State</div>
        <div role="columnheader" title="Distinct intel sources">Sources</div>
        <div role="columnheader">Last seen</div>
      </div>
      <FixedSizeList
        height={VIRTUALIZED_LIST_HEIGHT_PX}
        itemCount={items.length}
        itemSize={VIRTUALIZED_ROW_HEIGHT_PX}
        width="100%"
      >
        {({ index, style }) => (
          <EventRow
            item={items[index]}
            onSelect={onSelect}
            as="div"
            style={style}
            selection={selection}
            gridCols={gridCols}
          />
        )}
      </FixedSizeList>
    </div>
  );
}

interface EventRowProps {
  item: CanonicalEventListItem;
  onSelect: (id: string) => void;
  as: 'tr' | 'div';
  style?: React.CSSProperties;
  selection?: EventsSelection;
  gridCols?: string;
}

function SelectCell({ item, selection, as }: {
  item: CanonicalEventListItem;
  selection: EventsSelection;
  as: 'tr' | 'div';
}): React.JSX.Element {
  const checkbox = (
    <input
      type="checkbox"
      checked={selection.selectedIds.has(item.id)}
      onChange={() => selection.onToggle(item.id)}
      // Don't open the detail panel when toggling the checkbox.
      onClick={(e) => e.stopPropagation()}
      aria-label={`Select ${item.cve_id}`}
    />
  );
  return as === 'tr'
    ? <td style={{ padding: '0.5rem' }} onClick={(e) => e.stopPropagation()}>{checkbox}</td>
    : <div role="gridcell" onClick={(e) => e.stopPropagation()}>{checkbox}</div>;
}

function CveOpenButton({ item, onSelect }: {
  item: CanonicalEventListItem;
  onSelect: (id: string) => void;
}): React.JSX.Element {
  // Focusable open-detail control used in selection mode, where the ROW is not
  // itself a button (avoids nesting the checkbox inside an interactive row).
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        fontFamily: 'monospace',
        color: 'var(--accent-primary, #3b82f6)',
        textDecoration: 'underline',
        cursor: 'pointer',
      }}
    >
      {item.cve_id}
    </button>
  );
}

function EventRow({ item, onSelect, as, style, selection, gridCols }: EventRowProps): React.JSX.Element {
  const handleClick = () => onSelect(item.id);
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(item.id);
    }
  };
  // In selection mode the row carries a checkbox; the row itself must NOT be an
  // interactive widget (nested-interactive a11y violation). The CVE cell
  // becomes the focusable open control instead.
  const rowInteractive = !selection;
  const cveCellContent = selection
    ? <CveOpenButton item={item} onSelect={onSelect} />
    : item.cve_id;

  if (as === 'tr') {
    return (
      <tr
        {...(rowInteractive
          ? { role: 'button', tabIndex: 0, onClick: handleClick, onKeyDown: handleKey }
          : {})}
        style={{
          cursor: rowInteractive ? 'pointer' : 'default',
          borderTop: '1px solid var(--border-default, #e5e7eb)',
        }}
      >
        {selection && <SelectCell item={item} selection={selection} as="tr" />}
        <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{cveCellContent}</td>
        <td style={{ padding: '0.5rem' }}>
          {item.affected_target || (
            <em style={{ color: 'var(--text-secondary)' }}>— (platform-agnostic)</em>
          )}
        </td>
        <td style={{ padding: '0.5rem' }}>
          <SeverityBadge
            token={severityTokenFromScore(item.max_severity)}
            score={item.max_severity}
            showRoutingEligibilityChip={isRoutingEligible(item)}
          />
        </td>
        <td style={{ padding: '0.5rem' }}>
          <StatusBadge status={item.latest_status} />
        </td>
        <td style={{ padding: '0.5rem' }}>
          <OperatorStateBadge
            state={item.operator_state}
            snoozedUntil={item.snoozed_until}
          />
        </td>
        <td style={{ padding: '0.5rem' }}>{item.source_count}</td>
        <td style={{ padding: '0.5rem', fontSize: '0.75rem' }}>
          {item.last_seen_at ? new Date(item.last_seen_at).toLocaleString() : '—'}
        </td>
      </tr>
    );
  }

  // Virtualized div-row variant for FixedSizeList; mirrors the table
  // semantics via ARIA roles so screen readers still perceive a grid.
  return (
    <div
      role="row"
      {...(rowInteractive
        ? { tabIndex: 0, onClick: handleClick, onKeyDown: handleKey }
        : {})}
      aria-label={`CVE ${item.cve_id}`}
      style={{
        ...style,
        display: 'grid',
        gridTemplateColumns: gridCols ?? '160px 1fr 100px 110px 130px 80px 160px',
        gap: '0.5rem',
        cursor: rowInteractive ? 'pointer' : 'default',
        borderTop: '1px solid var(--border-default, #e5e7eb)',
        padding: '0.5rem',
        alignItems: 'center',
      }}
    >
      {selection && <SelectCell item={item} selection={selection} as="div" />}
      <div role="gridcell" style={{ fontFamily: 'monospace' }}>{cveCellContent}</div>
      <div role="gridcell">
        {item.affected_target || (
          <em style={{ color: 'var(--text-secondary)' }}>— (platform-agnostic)</em>
        )}
      </div>
      <div role="gridcell">
        <SeverityBadge
          token={severityTokenFromScore(item.max_severity)}
          score={item.max_severity}
          showRoutingEligibilityChip={isRoutingEligible(item)}
        />
      </div>
      <div role="gridcell">
        <StatusBadge status={item.latest_status} />
      </div>
      <div role="gridcell">
        <OperatorStateBadge
          state={item.operator_state}
          snoozedUntil={item.snoozed_until}
        />
      </div>
      <div role="gridcell">{item.source_count}</div>
      <div role="gridcell" style={{ fontSize: '0.75rem' }}>
        {item.last_seen_at ? new Date(item.last_seen_at).toLocaleString() : '—'}
      </div>
    </div>
  );
}
